import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Paper,
    Chip,
    IconButton,
    InputAdornment,
    ToggleButton,
    CircularProgress,
    Tooltip,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    CalendarToday as CalendarIcon,
    ArrowBackIos as ArrowLeftIcon,
    Save as SaveIcon,
    Add as AddIcon,
    Restore as RestoreIcon,
    ArrowForwardIos,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { format, addDays, subDays, startOfWeek, endOfWeek } from 'date-fns';
import { toast } from 'react-toastify';
import Brandcrumb from '../../../../../../componentsUiBlock/Brandcrumb';
import { request } from '../../../../../../utils/request';
import { AttendanceFormData, EmployeeBasic } from '../types';
import { usePermissions } from '../../../../../../utils/usePermissions';

// --- Interface Definitions ---


const AttendanceList: React.FC = () => {
    const { canManageAttendance } = usePermissions();
    const [, setSelectedDate] = useState<Date>(new Date());
    const [saving, setSaving] = useState(false);
    const [viewType] = useState<'day' | 'week' | 'month'>('day');
    const [, setEmployees] = useState<EmployeeBasic[]>([]);
    const [totalEmployee, setTotalEmployee] = useState(0);
    const [loading, setLoading] = useState(false);
    const [fetchingAttendance, setFetchingAttendance] = useState(false);
    const [initialLoad, setInitialLoad] = useState(true);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    // const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { isDirty },
    } = useForm<AttendanceFormData>({
        defaultValues: {
            date: format(new Date(), 'yyyy-MM-dd'),
            viewType: 'day',
            attendanceEntries: []
        }
    });

    const { fields } = useFieldArray({
        control,
        name: "attendanceEntries",
    });

    const attendanceEntries = watch('attendanceEntries');
    const currentDate = watch('date');

    // Calculate summary statistics
    const calculateSummary = () => {
        const totalPresent = attendanceEntries.filter(entry => entry.attendance_status === 'Present').length;
        const totalAbsent = attendanceEntries.filter(entry => entry.attendance_status === 'Absent').length;
        const totalHalfDay = attendanceEntries.filter(entry => entry.attendance_status === 'Half Day').length;
        const totalPaidLeave = attendanceEntries.filter(entry => entry.attendance_status === 'Paid Leave').length;
        const totalWeakOff = attendanceEntries.filter(entry => entry.attendance_status === 'Week Off').length;
        const totalHoliday = attendanceEntries.filter(entry => entry.attendance_status === 'Holiday').length;

        const totalWorkingHours = attendanceEntries.reduce((sum, entry) => sum + entry.working_hour, 0);
        const totalOvertimeHours = attendanceEntries.reduce((sum, entry) => sum + entry.overtime_hour, 0);
        const totalAdvanceAmount = attendanceEntries.reduce((sum, entry) => sum + entry.advance_amount, 0);

        return {
            totalPresent,
            totalAbsent,
            totalHalfDay,
            totalPaidLeave,
            totalWeakOff,
            totalHoliday,
            totalWorkingHours,
            totalOvertimeHours,
            totalAdvanceAmount,
        };
    };

    const summary = calculateSummary();

    const summarySection = [
        { label: 'Present', value: summary.totalPresent, short: "P", color: 'success' },
        { label: 'Absent', value: summary.totalAbsent, short: "A", color: 'error' },
        { label: 'Half Day', value: summary.totalHalfDay, short: "HD", color: 'warning' },
        { label: 'Paid Leave', value: summary.totalPaidLeave, short: "PL", color: 'info' },
        { label: 'Week Off', value: summary.totalWeakOff, short: "WO", color: 'secondary.contrastText' },
        { label: 'Holiday', value: summary.totalHoliday, short: "H", color: 'primary' },
    ];

    const navigate = useNavigate();

    // ============ FIXED: fetchEmployeesList without date dependency ============
    // ============ FIXED: fetchEmployeesList with better handling ============
    const fetchEmployeesList = useCallback(async (date?: string) => {
        try {
            setLoading(true);
            const res = await request({
                method: 'get',
                url: `employee/get-all-employees`
            });

            if (res.status === "success") {
                const fetchedEmployees = res.data || [];
                setEmployees(fetchedEmployees);
                setTotalEmployee(fetchedEmployees.length);

                // If date is provided, try to get existing attendance for that date first
                if (date) {
                    try {
                        const attendanceRes = await request({
                            method: 'get',
                            url: `attendance/get-attendance?date=${date}`
                        });

                        if (attendanceRes.status === "success" && attendanceRes.data) {
                            const existingAttendance = attendanceRes.data.attendanceEntries || [];

                            // Create a map of existing attendance by employee_id
                            const existingAttendanceMap = new Map();
                            existingAttendance.forEach((entry: any) => {
                                if (entry.employee_id) {
                                    const empId = entry.employee_id._id || entry.employee_id;
                                    existingAttendanceMap.set(empId, {
                                        employee_id: empId,
                                        employee_name: entry.employee_id?.first_name
                                            ? `${entry.employee_id.first_name || ''} ${entry.employee_id.last_name || ''}`.trim()
                                            : entry.employee_name || 'Unknown',
                                        in_time: entry.in_time || '09:00',
                                        out_time: entry.out_time || '18:00',
                                        working_hour: entry.working_hour || 0,
                                        overtime_hour: entry.overtime_hour || 0,
                                        advance_amount: entry.advance_amount || 0,
                                        attendance_status: entry.attendance_status || 'Present',
                                        note: entry.note || ''
                                    });
                                }
                            });

                            // Merge existing attendance with new employees
                            const attendanceEntries = fetchedEmployees.map((emp: EmployeeBasic) => {
                                const existingEntry = existingAttendanceMap.get(emp._id);
                                if (existingEntry) {
                                    return existingEntry;
                                }

                                // Default entry for new employee without attendance record
                                return {
                                    employee_id: emp._id,
                                    employee_name: `${emp.first_name} ${emp.last_name}`,
                                    in_time: '12:00',
                                    out_time: '12:00',
                                    working_hour: 0,
                                    overtime_hour: 0,
                                    advance_amount: 0,
                                    attendance_status: 'Absent' as const,
                                    note: '',
                                };
                            });

                            return attendanceEntries;
                        }
                    } catch (attendanceError) {
                        console.warn('Could not fetch attendance, using default:', attendanceError);
                    }
                }

                // Fallback: Create default attendance entries for all employees
                const attendanceEntries = fetchedEmployees.map((emp: EmployeeBasic) => ({
                    employee_id: emp._id,
                    employee_name: `${emp.first_name} ${emp.last_name}`,
                    in_time: '12:00',
                    out_time: '12:00',
                    working_hour: 0,
                    overtime_hour: 0,
                    advance_amount: 0,
                    attendance_status: 'Absent' as const,
                    note: '',
                }));

                return attendanceEntries;
            } else {
                toast.error('Failed to fetch employees');
                return [];
            }
        } catch (error: any) {
            console.error(error);
            toast.error(error?.message || 'Failed to fetch employees');
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    // ============ FIXED: fetchAttendanceForDate - don't reset the date ============
    // ============ SIMPLIFIED: fetchAttendanceForDate ============
    const fetchAttendanceForDate = useCallback(async (date: string, isInitialLoad = false) => {
        try {
            setFetchingAttendance(true);

            // Use the improved fetchEmployeesList which handles both employees and attendance
            const attendanceEntries = await fetchEmployeesList(date);

            if (attendanceEntries.length > 0) {
                reset({
                    date: date,
                    viewType: 'day',
                    attendanceEntries: attendanceEntries
                });

                if (!isInitialLoad) {
                    toast.success('Attendance loaded successfully');
                }
            } else {
                toast.error('No attendance data available');
            }
        } catch (error: any) {
            console.error('Error fetching attendance:', error);
            toast.error(error?.message || 'Failed to fetch attendance');
        } finally {
            setFetchingAttendance(false);
            if (isInitialLoad) {
                setInitialLoad(false);
            }
        }
    }, [fetchEmployeesList, reset]);

    // ============ FIXED: Initial load effect ============
    useEffect(() => {
        if (initialLoad) {
            const today = format(new Date(), 'yyyy-MM-dd');
            fetchAttendanceForDate(today, true);
        }
    }, [initialLoad, fetchAttendanceForDate]);

    // ============ FIXED: Date navigation handlers ============
    const handlePrevious = () => {
        const newDate = subDays(new Date(currentDate), viewType === 'day' ? 1 : viewType === 'week' ? 7 : 30);
        const formattedDate = format(newDate, 'yyyy-MM-dd');
        setValue('date', formattedDate, { shouldDirty: true });
        setSelectedDate(newDate);

        // Use setTimeout to avoid race condition
        setTimeout(() => {
            fetchAttendanceForDate(formattedDate);
        }, 0);
    };

    const handleNext = () => {
        const newDate = addDays(new Date(currentDate), viewType === 'day' ? 1 : viewType === 'week' ? 7 : 30);
        const formattedDate = format(newDate, 'yyyy-MM-dd');
        setValue('date', formattedDate, { shouldDirty: true });
        setSelectedDate(newDate);

        // Use setTimeout to avoid race condition
        setTimeout(() => {
            fetchAttendanceForDate(formattedDate);
        }, 0);
    };

    const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = event.target.value;
        setValue('date', newDate);
        setSelectedDate(new Date(newDate));

        // Use setTimeout to avoid race condition
        setTimeout(() => {
            fetchAttendanceForDate(newDate);
        }, 0);
    };

    // Format date display based on view type
    const getDateDisplay = () => {
        const date = new Date(currentDate);
        if (isMobile) {
            return format(date, 'dd/MM/yy');
        } else if (isTablet) {
            return format(date, 'EEE, MMM dd');
        }
        switch (viewType) {
            case 'day':
                return format(date, 'EEEE, MMMM dd, yyyy');
            case 'week':
                const weekStart = startOfWeek(date, { weekStartsOn: 1 });
                const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
                return `${format(weekStart, 'MMM dd')} - ${format(weekEnd, 'MMM dd, yyyy')}`;
            case 'month':
                return format(date, 'MMMM yyyy');
            default:
                return format(date, 'yyyy-MM-dd');
        }
    };

    // ============ Save/Update attendance ============
    const onSubmit = async (formData: AttendanceFormData) => {
        try {
            setSaving(true);

            const res = await request({
                method: 'post',
                url: "attendance/add-update-attendance",
                data: formData,
            });

            if (res?.status === "success") {
                toast.success(res?.message || 'Attendance saved successfully');
                // Reset dirty state after successful save
                reset(formData);
            } else {
                toast.error(res?.message || 'Failed to save attendance');
            }
        } catch (error: any) {
            console.error('Save attendance error:', error);
            toast.error(error || 'Failed to save attendance');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        // Reset to current date's attendance
        fetchAttendanceForDate(currentDate);
    };

    // Auto-calculate working hours when in/out time changes
    const calculateWorkingHours = (inTime: string, outTime: string) => {
        if (!inTime || !outTime) return 0;

        const [inHour, inMin] = inTime.split(':').map(Number);
        const [outHour, outMin] = outTime.split(':').map(Number);

        const inTotal = inHour * 60 + inMin;
        const outTotal = outHour * 60 + outMin;

        if (outTotal <= inTotal) return 0;

        return Math.round((outTotal - inTotal) / 60 * 10) / 10; // Round to 1 decimal
    };

    // Handle attendance status change
    const handleAttendanceStatusChange = (index: number, newStatus: "Present" | "Absent" | "Half Day" | "Paid Leave" | "Week Off" | "Holiday") => {
        // If status is Absent, Paid Leave, Week Off, or Holiday, reset time fields to 0
        if (['Absent', 'Paid Leave', 'Week Off', 'Holiday'].includes(newStatus)) {
            setValue(`attendanceEntries.${index}.in_time`, '00:00', { shouldDirty: true });
            setValue(`attendanceEntries.${index}.out_time`, '00:00', { shouldDirty: true });
            setValue(`attendanceEntries.${index}.working_hour`, 0, { shouldDirty: true });
            setValue(`attendanceEntries.${index}.overtime_hour`, 0, { shouldDirty: true });
        }

        // Update the status
        setValue(`attendanceEntries.${index}.attendance_status`, newStatus, { shouldDirty: true });
    };

    // Combined loading state
    const isLoading = loading || fetchingAttendance;

    return (
        <>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                <Brandcrumb />
            </Box>

            <Card elevation={4}>
                <CardContent>
                    {/* Header Section - Responsive */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        mb: 2,
                        gap: { xs: 2, sm: 0 }
                    }}>
                        <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
                            Employees Attendance
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            gap: 1,
                            width: { xs: '100%', sm: 'auto' },
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                            {canManageAttendance() && (
                                <Button
                                    variant="contained"
                                    startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                                    onClick={handleSubmit(onSubmit)}
                                    disabled={saving || isLoading}
                                    size="small"
                                    sx={{
                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                        width: { xs: '100%', sm: 'auto' }
                                    }}
                                >
                                    {saving ? 'Saving...' : 'Save Attendance'}
                                </Button>
                            )}

                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => navigate('/shopManagement/employeeManagement/employees/addEmployee')}
                                size="small"
                                sx={{
                                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                    height: 32,
                                    width: { xs: '100%', sm: 'auto' }
                                }}
                            >
                                Add New Employee
                            </Button>
                        </Box>
                    </Box>

                    {/* Date Selection Section - Responsive */}
                    <Paper elevation={0} sx={{
                        p: { xs: 1, sm: 1.5 },
                        mb: 2,
                        border: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            gap: { xs: 2, sm: 0 }
                        }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                minWidth: { xs: '100%', sm: 200 }
                            }}>
                                <CalendarIcon color="primary" sx={{ fontSize: { xs: 16, sm: 18 } }} />
                                <Typography variant="subtitle2" fontWeight="medium" noWrap>
                                    {getDateDisplay()}
                                </Typography>
                            </Box>

                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                width: { xs: '100%', sm: 'auto' },
                                flexDirection: { xs: 'column', sm: 'row' }
                            }}>
                                {/* Date Picker */}
                                <TextField
                                    size="small"
                                    type="date"
                                    value={currentDate}
                                    onChange={handleDateChange}
                                    disabled={isLoading}
                                    sx={{
                                        width: { xs: '100%', sm: 130 },
                                        '& .MuiInputBase-root': {
                                            fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                        }
                                    }}
                                />

                                {/* Navigation Buttons */}
                                <Box sx={{
                                    display: 'flex',
                                    gap: 0.5,
                                    width: { xs: '100%', sm: 'auto' },
                                    justifyContent: { xs: 'center', sm: 'flex-start' }
                                }}>
                                    <IconButton
                                        size="small"
                                        onClick={handlePrevious}
                                        disabled={isLoading}
                                        sx={{ p: 0.5 }}
                                    >
                                        <ArrowLeftIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={handleNext}
                                        disabled={isLoading}
                                        sx={{ p: 0.5 }}
                                    >
                                        <ArrowForwardIos sx={{ fontSize: { xs: 14, sm: 16 } }} />
                                    </IconButton>
                                </Box>
                            </Box>
                        </Box>
                    </Paper>

                    {/* Attendance Summary - Responsive */}
                    <Box sx={{
                        mb: 2,
                        p: 1,
                        bgcolor: 'grey.50',
                        borderRadius: 1
                    }}>
                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: { xs: 'center', sm: "space-between" },
                            gap: { xs: 1, sm: 2 }
                        }}>
                            {summarySection.map((summaryItem) => (
                                <Box
                                    key={summaryItem.label}
                                    sx={{
                                        p: { xs: 1, sm: 1.5 },
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        bgcolor: 'background.paper',
                                        display: 'flex',
                                        flexDirection: { xs: 'row', sm: 'column' },
                                        alignItems: { xs: 'center', sm: 'start' },
                                        justifyContent: 'center',
                                        minWidth: { xs: 'calc(50% - 8px)', sm: 120 },
                                        flex: {
                                            xs: '1 0 calc(50% - 8px)',
                                            sm: '1 0 calc(33.33% - 16px)',
                                            md: '1 0 calc(16.66% - 16px)'
                                        },
                                        maxWidth: {
                                            xs: 'calc(50% - 8px)',
                                            sm: 'calc(33.33% - 16px)',
                                            md: 'calc(16.66% - 16px)'
                                        },
                                        minHeight: { xs: 60, sm: 80 },
                                        boxSizing: 'border-box',
                                        gap: { xs: 1, sm: 0 }
                                    }}
                                >
                                    <Typography
                                        variant={isMobile ? "h4" : "h2"}
                                        fontWeight="normal"
                                        color={`${summaryItem.color}.main`}
                                        sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
                                    >
                                        {summaryItem.value}
                                    </Typography>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        mt: { xs: 0, sm: 0.5 }
                                    }}>
                                        <Chip
                                            label={summaryItem.short}
                                            size="small"
                                            color={summaryItem.color as any}
                                            sx={{
                                                height: { xs: 18, sm: 20 },
                                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                                minWidth: { xs: 25, sm: 30 }
                                            }}
                                        />
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                        >
                                            {summaryItem.label}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    <Box sx={{
                        mt: 2,
                        mb: 2,
                        p: { xs: 1, sm: 2 },
                        bgcolor: 'grey.50',
                        borderRadius: 1
                    }}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            gap: { xs: 1, sm: 0 }
                        }}>
                            <Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                >
                                    Total Employees: <strong>{totalEmployee}</strong>
                                </Typography>
                            </Box>
                            {isLoading && (
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                    <CircularProgress size={16} />
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                    >
                                        Loading attendance...
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Attendance Table - Responsive */}
                    <Box sx={{
                        overflowX: 'auto',
                        '&::-webkit-scrollbar': {
                            height: 6,
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: 'grey.100',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'grey.400',
                            borderRadius: 3,
                        },
                    }}>
                        <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                minWidth: isMobile ? 900 : 'auto'
                            }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                                        <TableCell sx={{
                                            fontWeight: 'bold',
                                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                            py: 1,
                                            width: isMobile ? '20%' : '16%',
                                            minWidth: 120
                                        }}>Employee Name</TableCell>
                                        <TableCell sx={{
                                            fontWeight: 'bold',
                                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                            py: 1,
                                            width: isMobile ? '10%' : '8%',
                                            minWidth: 80
                                        }}>In Time</TableCell>
                                        <TableCell sx={{
                                            fontWeight: 'bold',
                                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                            py: 1,
                                            width: isMobile ? '10%' : '8%',
                                            minWidth: 80
                                        }}>Out Time</TableCell>
                                        <TableCell sx={{
                                            fontWeight: 'bold',
                                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                            py: 1,
                                            width: isMobile ? '14%' : '12%',
                                            minWidth: 100
                                        }}>Working Hours</TableCell>
                                        <TableCell sx={{
                                            fontWeight: 'bold',
                                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                            py: 1,
                                            width: isMobile ? '14%' : '12%',
                                            minWidth: 100
                                        }}>Overtime Hours</TableCell>
                                        <TableCell sx={{
                                            fontWeight: 'bold',
                                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                            py: 1,
                                            width: isMobile ? '12%' : '10%',
                                            minWidth: 90
                                        }}>Advance (₹)</TableCell>
                                        <TableCell sx={{
                                            fontWeight: 'bold',
                                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                            py: 1,
                                            width: isMobile ? '16%' : '14%',
                                            minWidth: 140
                                        }}>Mark Attendance</TableCell>
                                        <TableCell sx={{
                                            fontWeight: 'bold',
                                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                            py: 1,
                                            width: isMobile ? '12%' : '10%',
                                            minWidth: 100
                                        }}>Note</TableCell>
                                        <TableCell sx={{
                                            fontWeight: 'bold',
                                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                            py: 1,
                                            width: isMobile ? '12%' : '10%',
                                            minWidth: 100
                                        }}>Monthly Attendance</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                                                <CircularProgress size={24} />
                                                <Typography variant="body2" sx={{ mt: 1 }}>
                                                    Loading attendance data...
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : fields.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    No attendance data found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        fields.map((field, index) => (
                                            <TableRow key={field.id} hover>
                                                {/* Employee Info */}
                                                <TableCell sx={{ py: 0.5 }}>
                                                    <Box>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight="medium"
                                                            noWrap
                                                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                                        >
                                                            {field.employee_name}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>

                                                {/* In Time */}
                                                <TableCell sx={{ py: 0.5 }}>
                                                    <Controller
                                                        name={`attendanceEntries.${index}.in_time`}
                                                        control={control}
                                                        render={({ field: { value, onChange, ...rest } }) => (
                                                            <TextField
                                                                {...rest}
                                                                size="small"
                                                                type="time"
                                                                value={value || ''}
                                                                onChange={(e) => {
                                                                    onChange(e.target.value);
                                                                    // Auto-calculate working hours
                                                                    const outTime = watch(`attendanceEntries.${index}.out_time`);
                                                                    const workingHours = calculateWorkingHours(e.target.value, outTime);
                                                                    setValue(`attendanceEntries.${index}.working_hour`, workingHours, { shouldDirty: true });
                                                                }}
                                                                fullWidth
                                                                InputProps={{
                                                                    sx: {
                                                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                                        height: 32
                                                                    },
                                                                }}
                                                                inputProps={{
                                                                    step: 1800,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </TableCell>

                                                {/* Out Time */}
                                                <TableCell sx={{ py: 0.5 }}>
                                                    <Controller
                                                        name={`attendanceEntries.${index}.out_time`}
                                                        control={control}
                                                        render={({ field: { value, onChange, ...rest } }) => (
                                                            <TextField
                                                                {...rest}
                                                                size="small"
                                                                type="time"
                                                                value={value || ''}
                                                                onChange={(e) => {
                                                                    onChange(e.target.value);
                                                                    // Auto-calculate working hours
                                                                    const inTime = watch(`attendanceEntries.${index}.in_time`);
                                                                    const workingHours = calculateWorkingHours(inTime, e.target.value);
                                                                    setValue(`attendanceEntries.${index}.working_hour`, workingHours, { shouldDirty: true });
                                                                }}
                                                                fullWidth
                                                                InputProps={{
                                                                    sx: {
                                                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                                        height: 32
                                                                    },
                                                                }}
                                                                inputProps={{
                                                                    step: 3600,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </TableCell>

                                                {/* Working Hours */}
                                                <TableCell sx={{ py: 0.5 }}>
                                                    <Controller
                                                        name={`attendanceEntries.${index}.working_hour`}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField
                                                                {...field}
                                                                size="small"
                                                                type="number"
                                                                inputProps={{ min: 0, step: 0.5 }}
                                                                InputProps={{
                                                                    sx: {
                                                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                                        height: 32
                                                                    },
                                                                    endAdornment: <InputAdornment position="end">h</InputAdornment>,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </TableCell>

                                                {/* Overtime Hours */}
                                                <TableCell sx={{ py: 0.5 }}>
                                                    <Controller
                                                        name={`attendanceEntries.${index}.overtime_hour`}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField
                                                                {...field}
                                                                size="small"
                                                                type="number"
                                                                inputProps={{ min: 0, step: 0.5 }}
                                                                InputProps={{
                                                                    sx: {
                                                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                                        height: 32
                                                                    },
                                                                    endAdornment: <InputAdornment position="end">h</InputAdornment>,
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </TableCell>

                                                {/* Advance Amount */}
                                                <TableCell sx={{ py: 0.5 }}>
                                                    <Controller
                                                        name={`attendanceEntries.${index}.advance_amount`}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField
                                                                {...field}
                                                                size="small"
                                                                type="number"
                                                                inputProps={{ min: 0 }}
                                                                InputProps={{
                                                                    sx: {
                                                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                                        height: 32
                                                                    },
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </TableCell>

                                                {/* Attendance Status */}
                                                <TableCell sx={{ py: 0.5 }}>
                                                    <Controller
                                                        name={`attendanceEntries.${index}.attendance_status`}
                                                        control={control}
                                                        render={({ field: { value } }) => (
                                                            <Box sx={{
                                                                display: 'grid',
                                                                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                                                                gap: 0.5,
                                                                width: '100%'
                                                            }}>
                                                                {isMobile && (
                                                                    <Box sx={{
                                                                        gridColumn: 'span 2',
                                                                        display: 'flex',
                                                                        gap: 0.5,
                                                                        mb: 0.5
                                                                    }}>
                                                                        <Typography
                                                                            variant="caption"
                                                                            sx={{
                                                                                fontSize: '0.6rem',
                                                                                fontWeight: 'bold',
                                                                                color: 'text.secondary'
                                                                            }}
                                                                        >
                                                                            Status:
                                                                        </Typography>
                                                                        <Typography
                                                                            variant="caption"
                                                                            sx={{
                                                                                fontSize: '0.6rem',
                                                                                fontWeight: 'bold'
                                                                            }}
                                                                        >
                                                                            {value}
                                                                        </Typography>
                                                                    </Box>
                                                                )}

                                                                {/* Present */}
                                                                <Tooltip title="Present" placement="top" arrow>
                                                                    <ToggleButton
                                                                        value="Present"
                                                                        selected={value === 'Present'}
                                                                        onChange={() => handleAttendanceStatusChange(index, 'Present')}
                                                                        size="small"
                                                                        sx={{
                                                                            minWidth: 0,
                                                                            width: '100%',
                                                                            height: isMobile ? 24 : 28,
                                                                            fontSize: isMobile ? '0.6rem' : '0.7rem',
                                                                            padding: 0,
                                                                            borderRadius: 1,
                                                                            bgcolor: value === 'Present' ? 'success.main' : 'transparent',
                                                                            color: value === 'Present' ? 'white' : 'text.primary',
                                                                            border: '1px solid',
                                                                            borderColor: value === 'Present' ? 'success.main' : 'grey.300',
                                                                            '&.Mui-selected': {
                                                                                color: 'white',
                                                                                bgcolor: 'success.main',
                                                                                '&:hover': { bgcolor: 'success.dark' }
                                                                            },
                                                                            '&:hover': {
                                                                                bgcolor: value === 'Present' ? 'success.dark' : 'grey.100'
                                                                            }
                                                                        }}
                                                                    >
                                                                        {isMobile ? 'P' : 'P'}
                                                                    </ToggleButton>
                                                                </Tooltip>

                                                                {/* Absent */}
                                                                <Tooltip title="Absent" placement="top" arrow>
                                                                    <ToggleButton
                                                                        value="Absent"
                                                                        selected={value === 'Absent'}
                                                                        onChange={() => handleAttendanceStatusChange(index, 'Absent')}
                                                                        size="small"
                                                                        sx={{
                                                                            minWidth: 0,
                                                                            width: '100%',
                                                                            height: isMobile ? 24 : 28,
                                                                            fontSize: isMobile ? '0.6rem' : '0.7rem',
                                                                            padding: 0,
                                                                            borderRadius: 1,
                                                                            bgcolor: value === 'Absent' ? 'error.main' : 'transparent',
                                                                            color: value === 'Absent' ? 'white' : 'text.primary',
                                                                            border: '1px solid',
                                                                            borderColor: value === 'Absent' ? 'error.main' : 'grey.300',
                                                                            '&.Mui-selected': {
                                                                                color: 'white',
                                                                                bgcolor: 'error.main',
                                                                                '&:hover': { bgcolor: 'error.dark' }
                                                                            },
                                                                            '&:hover': {
                                                                                bgcolor: value === 'Absent' ? 'error.dark' : 'grey.100'
                                                                            }
                                                                        }}
                                                                    >
                                                                        {isMobile ? 'A' : 'A'}
                                                                    </ToggleButton>
                                                                </Tooltip>

                                                                {/* Half Day */}
                                                                <Tooltip title="Half Day" placement="top" arrow>
                                                                    <ToggleButton
                                                                        value="Half Day"
                                                                        selected={value === 'Half Day'}
                                                                        onChange={() => handleAttendanceStatusChange(index, 'Half Day')}
                                                                        size="small"
                                                                        sx={{
                                                                            minWidth: 0,
                                                                            width: '100%',
                                                                            height: isMobile ? 24 : 28,
                                                                            fontSize: isMobile ? '0.6rem' : '0.7rem',
                                                                            padding: 0,
                                                                            borderRadius: 1,
                                                                            bgcolor: value === 'Half Day' ? 'warning.main' : 'transparent',
                                                                            color: value === 'Half Day' ? 'white' : 'text.primary',
                                                                            border: '1px solid',
                                                                            borderColor: value === 'Half Day' ? 'warning.main' : 'grey.300',
                                                                            '&.Mui-selected': {
                                                                                color: 'white',
                                                                                bgcolor: 'warning.main',
                                                                                '&:hover': { bgcolor: 'warning.dark' }
                                                                            },
                                                                            '&:hover': {
                                                                                bgcolor: value === 'Half Day' ? 'warning.dark' : 'grey.100'
                                                                            }
                                                                        }}
                                                                    >
                                                                        {isMobile ? 'HD' : 'HD'}
                                                                    </ToggleButton>
                                                                </Tooltip>

                                                                {/* Paid Leave */}
                                                                <Tooltip title="Paid Leave" placement="top" arrow>
                                                                    <ToggleButton
                                                                        value="Paid Leave"
                                                                        selected={value === 'Paid Leave'}
                                                                        onChange={() => handleAttendanceStatusChange(index, 'Paid Leave')}
                                                                        size="small"
                                                                        sx={{
                                                                            minWidth: 0,
                                                                            width: '100%',
                                                                            height: isMobile ? 24 : 28,
                                                                            fontSize: isMobile ? '0.6rem' : '0.7rem',
                                                                            padding: 0,
                                                                            borderRadius: 1,
                                                                            bgcolor: value === 'Paid Leave' ? 'info.main' : 'transparent',
                                                                            color: value === 'Paid Leave' ? 'white' : 'text.primary',
                                                                            border: '1px solid',
                                                                            borderColor: value === 'Paid Leave' ? 'info.main' : 'grey.300',
                                                                            '&.Mui-selected': {
                                                                                color: 'white',
                                                                                bgcolor: 'info.main',
                                                                                '&:hover': { bgcolor: 'info.dark' }
                                                                            },
                                                                            '&:hover': {
                                                                                bgcolor: value === 'Paid Leave' ? 'info.dark' : 'grey.100'
                                                                            }
                                                                        }}
                                                                    >
                                                                        {isMobile ? 'PL' : 'PL'}
                                                                    </ToggleButton>
                                                                </Tooltip>

                                                                {/* Week Off */}
                                                                <Tooltip title="Week Off" placement="top" arrow>
                                                                    <ToggleButton
                                                                        value="Week Off"
                                                                        selected={value === 'Week Off'}
                                                                        onChange={() => handleAttendanceStatusChange(index, 'Week Off')}
                                                                        size="small"
                                                                        sx={{
                                                                            minWidth: 0,
                                                                            width: '100%',
                                                                            height: isMobile ? 24 : 28,
                                                                            fontSize: isMobile ? '0.6rem' : '0.7rem',
                                                                            padding: 0,
                                                                            borderRadius: 1,
                                                                            bgcolor: value === 'Week Off' ? 'grey.400' : 'transparent',
                                                                            color: value === 'Week Off' ? 'white' : 'text.primary',
                                                                            border: '1px solid',
                                                                            borderColor: value === 'Week Off' ? 'secondary.contrastText' : 'grey.300',
                                                                            '&.Mui-selected': {
                                                                                color: 'white',
                                                                                bgcolor: 'secondary.contrastText',
                                                                                '&:hover': { bgcolor: 'secondary.contrastText' }
                                                                            },
                                                                            '&:hover': {
                                                                                bgcolor: value === 'Week Off' ? 'grey.500' : 'grey.100'
                                                                            }
                                                                        }}
                                                                    >
                                                                        {isMobile ? 'WO' : 'WO'}
                                                                    </ToggleButton>
                                                                </Tooltip>

                                                                {/* Holiday */}
                                                                <Tooltip title="Holiday" placement="top" arrow>
                                                                    <ToggleButton
                                                                        value="Holiday"
                                                                        selected={value === 'Holiday'}
                                                                        onChange={() => handleAttendanceStatusChange(index, 'Holiday')}
                                                                        size="small"
                                                                        sx={{
                                                                            minWidth: 0,
                                                                            width: '100%',
                                                                            height: isMobile ? 24 : 28,
                                                                            fontSize: isMobile ? '0.6rem' : '0.7rem',
                                                                            padding: 0,
                                                                            borderRadius: 1,
                                                                            bgcolor: value === 'Holiday' ? 'primary.main' : 'transparent',
                                                                            color: value === 'Holiday' ? 'white' : 'text.primary',
                                                                            border: '1px solid',
                                                                            borderColor: value === 'Holiday' ? 'primary.main' : 'grey.300',
                                                                            '&.Mui-selected': {
                                                                                color: 'white',
                                                                                bgcolor: 'primary.main',
                                                                                '&:hover': { bgcolor: 'primary.dark' }
                                                                            },
                                                                            '&:hover': {
                                                                                bgcolor: value === 'Holiday' ? 'primary.dark' : 'grey.100'
                                                                            }
                                                                        }}
                                                                    >
                                                                        {isMobile ? 'H' : 'H'}
                                                                    </ToggleButton>
                                                                </Tooltip>
                                                            </Box>
                                                        )}
                                                    />
                                                </TableCell>

                                                {/* Note */}
                                                <TableCell sx={{ py: 0.5 }}>
                                                    <Controller
                                                        name={`attendanceEntries.${index}.note`}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <TextField
                                                                {...field}
                                                                size="small"
                                                                placeholder="Note..."
                                                                InputProps={{
                                                                    sx: {
                                                                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                                        height: 32,
                                                                        width: '100%'
                                                                    },
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </TableCell>

                                                {/* Monthly Attendance */}
                                                <TableCell sx={{ py: 0.5 }}>
                                                    <Button
                                                        variant="text"
                                                        onClick={() => navigate(`/shopManagement/employeeManagement/attendance/${field.employee_id}`)}
                                                        disabled={isLoading}
                                                        sx={{
                                                            minWidth: 'auto',
                                                            fontSize: { xs: '0.55rem', sm: '0.60rem' },
                                                            textWrap: 'nowrap',
                                                            py: 0.1,
                                                            px: { xs: 1, sm: 2 },
                                                            borderRadius: 5,
                                                            border: '.8px solid #7f5ee666',
                                                            '&:hover': { backgroundColor: '#7f5ee666' }
                                                        }}
                                                    >
                                                        Check
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    {/* Form Actions */}
                    {isDirty && (
                        <Box sx={{
                            mt: 2,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 1
                        }}>
                            <Button
                                variant="outlined"
                                startIcon={<RestoreIcon />}
                                onClick={handleReset}
                                disabled={isLoading}
                                size="small"
                                sx={{
                                    fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                }}
                            >
                                Discard Changes
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </>
    );
};

export default AttendanceList;